import React, { useEffect, useState } from 'react';
import { Col, Row, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import CampaignRecordingExplorerCalls from 'pages/hvamanagement/CampaignRecordingExplorerCalls';
import {
  fetchHVACampaignNames,
  fetchHVAServiceNames,
  HVACampaignName,
  HVAServiceName
} from 'services/hvaservicecampaign.service';
import { getAllHVAAgents, HVAAgent } from 'services/hvaagent.service';

const CampaignRecordingExplorer = () => {
  const [campaigns, setCampaigns] = useState<HVACampaignName[]>([]);
  const [services, setServices] = useState<HVAServiceName[]>([]);
  const [hvaagents, setHVAAgents] = useState<HVAAgent[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [formData, setFormData] = useState({
    service: '',
    campaign: '',
    hva: '',
    firstName: '',
    lastName: '',
    accountID: '',
    customerID: '',
    phoneNumber: ''
  });

  const [submittedData, setSubmittedData] = useState({
    service: '',
    campaign: '',
    hva: '',
    firstName: '',
    lastName: '',
    accountID: '',
    customerID: '',
    phoneNumber: '',
    startDate: '',
    endDate: ''
  });

  // Forzar el renderizado del componente hijo con una nueva clave
  const [renderKey, setRenderKey] = useState(0);

  // Para manejar las fechas sin hora
  const formatDateWithoutTime = (date: Date): string => {
    const offset = date.getTimezoneOffset() * 60000; // Offset en milisegundos
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().split('T')[0];
  };

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const campaignNames = await fetchHVACampaignNames();
        setCampaigns(campaignNames);
      } catch (error) {
        alert('Error loading campaigns:');
      }
    };

    loadCampaigns();
  }, []);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const serviceNames = await fetchHVAServiceNames();
        setServices(serviceNames);
      } catch (error) {
        alert('Error loading services:');
      }
    };

    loadServices();
  }, []);

  useEffect(() => {
    const loadHVAAgents = async () => {
      try {
        const hvaAgents = await getAllHVAAgents();
        setHVAAgents(hvaAgents);
      } catch (error) {
        alert('Error loading services:');
      }
    };

    loadHVAAgents();
  }, []);

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { id: string; value: string } }
  ) => {
    const { id, value } = e.target;

    setFormData(prevState => ({
      ...prevState,
      [id]: value
    }));
  };

  const handleDateChange = (id: string, date: Date[]) => {
    const selectedDate = date.length > 0 ? date[0] : null;
    if (id === 'startDate') {
      setStartDate(selectedDate);
    } else if (id === 'endDate') {
      setEndDate(selectedDate);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    setSubmittedData({
      ...formData,
      startDate: formatDateWithoutTime(startDate),
      endDate: formatDateWithoutTime(endDate)
    });

    // Cambiar la clave para forzar el renderizado del componente hijo
    setRenderKey(prevKey => prevKey + 1);
  };

  return (
    <>
      <Row className="gy-3 mb-3 justify-content-between">
        <Col>
          <h2 className="mb-2 text-body-emphasis">Search for any call</h2>
          <h5 className="text-body-tertiary fw-semibold">
            Fill in the form below to start searching for calls
          </h5>
        </Col>
      </Row>
      <Row as="form" className="g-2 mb-4" onSubmit={handleSubmit}>
        <Col xs={4} lg={4} xl={2} className="px-1">
          <label className="form-label" htmlFor="service">
            Service
          </label>
          <Form.Select
            id="service"
            value={formData.service}
            onChange={handleInputChange}
            title="Service"
            aria-label="Select Service"
          >
            <option></option>
            {services.map(service => (
              <option key={service.id} value={service.id}>
                {service.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={4} lg={4} xl={2} className="px-1">
          <label className="form-label" htmlFor="campaign">
            Campaign
          </label>
          <Form.Select
            id="campaign"
            value={formData.campaign}
            onChange={handleInputChange}
            title="Campaign"
            aria-label="Select Campaign"
          >
            <option></option>
            {campaigns.map(campaign => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={4} lg={4} xl={2} className="px-1">
          <label className="form-label" htmlFor="hva">
            HVA
          </label>
          <Form.Select
            id="hva"
            value={formData.hva}
            onChange={handleInputChange}
            title="HVA"
            aria-label="Select HVA"
          >
            <option></option>
            {hvaagents.map(hva => (
              <option key={hva.id} value={hva.id}>
                {hva.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={4} lg={4} xl={2} className="px-1">
          <label className="form-label" htmlFor="startDate">
            Start Date
          </label>
          <DatePicker
            value={startDate ? [startDate] : []}
            onChange={date => handleDateChange('startDate', date)}
            options={{ dateFormat: 'M j, Y' }}
          />
        </Col>
        <Col xs={4} lg={4} xl={2} className="px-1">
          <label className="form-label" htmlFor="endDate">
            End Date
          </label>
          <DatePicker
            value={endDate ? [endDate] : []}
            onChange={date => handleDateChange('endDate', date)}
            options={{ dateFormat: 'M j, Y' }}
          />
        </Col>
        <Col></Col>
        <Col xs={4} lg={4} xl={2} className="px-1">
          <label className="form-label" htmlFor="firstName">
            First name
          </label>
          <Form.Control
            type="text"
            id="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
          />
        </Col>
        <Col xs={4} lg={4} xl={2} className="px-1">
          <label className="form-label" htmlFor="lastName">
            Last name
          </label>
          <Form.Control
            type="text"
            id="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
          />
        </Col>
        <Col xs={4} lg={4} xl={2} className="px-1">
          <label className="form-label" htmlFor="accountID">
            Account ID
          </label>
          <Form.Control
            type="text"
            id="accountID"
            value={formData.accountID}
            onChange={handleInputChange}
          />
        </Col>
        <Col xs={4} lg={4} xl={2} className="px-1">
          <label className="form-label" htmlFor="customerID">
            Customer ID
          </label>
          <Form.Control
            type="text"
            id="customerID"
            value={formData.customerID}
            onChange={handleInputChange}
          />
        </Col>
        <Col xs={4} lg={4} xl={2} className="px-1">
          <label className="form-label" htmlFor="phoneNumber">
            Phone number
          </label>
          <Form.Control
            type="text"
            id="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
          />
        </Col>
        <Col xs={4} lg={4} xl={2} className="px-1">
          <label className="form-label"> </label>
          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              className="w-100"
              type="submit"
              style={{ height: '36px' }}
            >
              <FontAwesomeIcon icon={faSearch} className="me-2 fs-8" />
              Find calls
            </Button>
          </div>
        </Col>
      </Row>
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis pt-1 mt-0 mb-0 border-top">
        <Row className="gy-3 mt-1 justify-content-between bg-body-emphasis">
          <CampaignRecordingExplorerCalls
            key={renderKey} // Clave dinámica para forzar el renderizado
            service={submittedData.service}
            campaign={submittedData.campaign}
            hva={submittedData.hva}
            firstName={submittedData.firstName}
            lastName={submittedData.lastName}
            accountID={submittedData.accountID}
            customerID={submittedData.customerID}
            phoneNumber={submittedData.phoneNumber}
            startDate={submittedData.startDate}
            endDate={submittedData.endDate}
          />
        </Row>
      </div>
    </>
  );
};

export default CampaignRecordingExplorer;
